// Which combinations to train: all of them, or one label. A pill in the
// toolbar that opens a panel of label chips (with a search field when there
// are many), instead of a dropdown.
import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "../i18n";

type Props = {
  value: string;
  total: number;
  labels: { label: string; count: number }[];
  onChange: (label: string) => void;
};

const SEARCH_FROM = 10;

export function LabelPicker({ value, total, labels, onChange }: Props) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", esc);
    search.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? labels.filter((l) => l.label.toLowerCase().includes(q)) : labels;
  }, [labels, query]);

  const current = labels.find((l) => l.label === value);
  const pick = (label: string) => {
    onChange(label);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="labelpick" ref={box}>
      <button
        className={`labelpick-pill ${value ? "on" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={t("train.label")}
        onClick={() => setOpen(!open)}
      >
        <span className="name">{value || t("train.all")}</span>
        <span className="count num">{value ? (current?.count ?? 0) : total}</span>
        <span aria-hidden className="chev">▾</span>
      </button>
      {open && (
        <div className="labelpick-panel card" role="dialog" aria-label={t("train.label")}>
          {labels.length >= SEARCH_FROM && (
            <input
              ref={search}
              className="labelpick-search"
              type="search"
              placeholder={t("train.label_search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && shown.length === 1) pick(shown[0].label);
              }}
            />
          )}
          <div className="labelpick-chips">
            {!query && (
              <button className={`chip ${value === "" ? "on" : ""}`} onClick={() => pick("")}>
                {t("train.all")} <span className="num">{total}</span>
              </button>
            )}
            {shown.map((l) => (
              <button key={l.label} className={`chip ${l.label === value ? "on" : ""}`} onClick={() => pick(l.label)}>
                {l.label} <span className="num">{l.count}</span>
              </button>
            ))}
            {shown.length === 0 && <span className="muted small">{t("train.label_none")}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
